import { type DotLottie, DotLottieReact } from "@lottiefiles/dotlottie-react";
import { useEffect, useState } from "react";

import { useSalesContext } from "../../../sales-context/SalesContext";
import { useProductOfferStore } from "../../../stores";
import { SaleProgress } from "../../../types";
import src from "./animations/Confetti.json?url";
import styles from "./Confetti.module.css";

export function ConfettiAnimation() {
    const { sessionId } = useSalesContext();
    const [dotLottie, setDotLottie] = useState<DotLottie | null>(null);
    const storesHaveFinishedRehydrating = useProductOfferStore.persist.hasHydrated();
    const showConfettiForSuccessfulSale = useProductOfferStore((state) =>
        state.getPrimaryProducts(sessionId).some((p) => p.saleProgress === SaleProgress.Success && !p.isConfettiShown),
    );
    // Play confetti animation if the product offer store has been rehydrated and there is some product with a
    // successful sale that haven't shown confetti yet.
    const playConfetti = storesHaveFinishedRehydrating && showConfettiForSuccessfulSale;

    useEffect(() => {
        // Update the confetti status in the product offer store when the component unmounts.
        // This prevents the confetti animation from replaying for the same sale when switching between chats,
        // while the animation was still in progress.
        return () => useProductOfferStore.getState().updateProductsConfettiStatus(sessionId);
    }, [sessionId]);

    // Mark confetti as shown once it finishes playing.
    useEffect(() => {
        if (!dotLottie) return;
        const onComplete = () => useProductOfferStore.getState().updateProductsConfettiStatus(sessionId);
        dotLottie.addEventListener("complete", onComplete);
        return () => dotLottie.removeEventListener("complete", onComplete);
    }, [dotLottie, sessionId]);

    // Play the animation once it is loaded and a successful sale is pending confetti. Gate on `isLoaded`
    // (this animation's data is ready), not `isReady` (only the WASM core is up) — a warmed core can report
    // ready before `src` has loaded, which would drop the play() and never show confetti. Seek to frame 0
    // first: `play()` resumes from the current frame, so a second sale in the same session (the instance is
    // still parked on the last frame) would otherwise not visibly replay.
    useEffect(() => {
        if (!dotLottie || !playConfetti) return;
        const playFromStart = () => {
            dotLottie.setFrame(0);
            dotLottie.play();
        };
        if (dotLottie.isLoaded) {
            playFromStart();
            return;
        }
        dotLottie.addEventListener("load", playFromStart);
        return () => dotLottie.removeEventListener("load", playFromStart);
    }, [dotLottie, playConfetti]);

    // FullStory renders a <canvas> as a diagonal hatch; don't leave this full-viewport one mounted while idle.
    if (!playConfetti) return null;

    return (
        <DotLottieReact
            className={styles.player}
            src={src}
            speed={0.5}
            loop={false}
            autoplay={false}
            dotLottieRefCallback={setDotLottie}
        />
    );
}
